import { useRef, useState } from 'react';
import { Download, FileImage, FileText, Loader2, Wallet, X } from 'lucide-react';
import toast from 'react-hot-toast';
import LoyaltyMemberCard, { type LoyaltyCardDesign, type LoyaltyCardTier } from './LoyaltyMemberCard';
import { addCardToWallet, downloadCardPdf, downloadCardPng } from './loyaltyCardExport';

export type LoyaltyCardModalProps = {
  open: boolean;
  onClose: () => void;
  design?: LoyaltyCardDesign | string;
  colour?: string;
  logoUrl?: string | null;
  shopName?: string;
  tagline?: string;
  programName?: string;
  customerName?: string;
  memberId?: string;
  points?: number;
  value?: number;
  tier?: LoyaltyCardTier;
  qrDataUrl?: string | null;
  showPoints?: boolean;
  showValue?: boolean;
  showTier?: boolean;
};

export default function LoyaltyCardModal({
  open,
  onClose,
  ...cardProps
}: LoyaltyCardModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<'png' | 'pdf' | 'wallet' | null>(null);
  const primary = cardProps.colour || '#15803d';

  if (!open) return null;

  const fileBase = `loyalty-card-${(cardProps.memberId || cardProps.customerName || 'member')
    .toString()
    .replace(/[^\w.-]+/g, '_')}`;

  const run = async (kind: 'png' | 'pdf' | 'wallet', fn: () => Promise<void>) => {
    if (!cardRef.current) return;
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return;
      toast.error(kind === 'wallet' ? 'Could not save for Wallet' : kind === 'pdf' ? 'Could not export PDF' : 'Could not export image');
      console.error(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-slate-50 rounded-2xl shadow-2xl w-full max-w-md max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Loyalty member card"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-white rounded-t-2xl sticky top-0 z-10">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Loyalty Card</h3>
            {cardProps.memberId ? (
              <p className="text-[11px] text-slate-500 font-mono mt-0.5">
                QR = {cardProps.memberId} · scan at POS to select
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 flex justify-center bg-slate-100/80">
          <div ref={cardRef}>
            <LoyaltyMemberCard {...cardProps} />
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-white rounded-b-2xl space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center gap-1.5 text-sm py-2.5 rounded-xl font-semibold border-2 border-slate-800 bg-white text-slate-900 disabled:opacity-60"
              disabled={!!busy}
              onClick={() =>
                void run('pdf', async () => {
                  if (!cardRef.current) return;
                  await downloadCardPdf(cardRef.current, fileBase);
                  toast.success('PDF downloaded');
                })
              }
            >
              {busy === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Download Card
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-1.5 text-sm py-2.5 rounded-xl font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: primary }}
              disabled={!!busy}
              onClick={() =>
                void run('wallet', async () => {
                  if (!cardRef.current) return;
                  const result = await addCardToWallet(cardRef.current, fileBase);
                  toast.success(result === 'shared' ? 'Shared to device' : 'Card image saved');
                })
              }
            >
              {busy === 'wallet' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
              Add to Wallet
            </button>
          </div>
          <button
            type="button"
            className="w-full btn-secondary inline-flex items-center justify-center gap-1.5 text-sm py-2"
            disabled={!!busy}
            onClick={() =>
              void run('png', async () => {
                if (!cardRef.current) return;
                await downloadCardPng(cardRef.current, fileBase);
              })
            }
          >
            {busy === 'png' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileImage className="w-4 h-4" />}
            Download image
          </button>
          <p className="w-full text-[11px] text-slate-400 text-center flex items-center justify-center gap-1">
            <Download className="w-3 h-3" />
            Also available on the customer portal
          </p>
        </div>
      </div>
    </div>
  );
}
